import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { DollarSign, Play, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const STATUS_COLOR = {
  pending: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

const AdminSettlements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.get('/admin/settlements');
      setItems(data.items || []);
    } catch {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRun = async () => {
    if (!window.confirm('Run settlements now for the past 24 hours?')) return;
    setRunning(true);
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.post('/admin/settlements/run', { period_days: 1 });
      toast({ title: 'Settlement run complete', description: `${data.created} new settlement(s) created.` });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setRunning(false); }
  };

  const handleMarkPaid = async (id) => {
    const txn = window.prompt('Mobile Money transaction ID (optional):');
    try {
      const api = (await import('../../utils/api')).default;
      const params = txn ? `?txn_id=${encodeURIComponent(txn)}` : '';
      await api.post(`/admin/settlements/${id}/mark-paid${params}`);
      toast({ title: 'Marked paid' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const totalPending = items.filter(i => i.status !== 'paid').reduce((s, i) => s + (i.netPayout || 0), 0);
  const totalPaid = items.filter(i => i.status === 'paid').reduce((s, i) => s + (i.netPayout || 0), 0);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-settlements-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><DollarSign className="h-6 w-6 text-pink-600" /> Operator Settlements</h1>
            <p className="text-sm text-gray-500">Auto-generated daily at 23:00. Mark as paid once Mobile Money payout completes.</p>
          </div>
          <Button onClick={handleRun} disabled={running} className="bg-pink-600 hover:bg-pink-700" data-testid="settlements-run-btn">
            <Play className="h-4 w-4 mr-1" /> {running ? 'Running...' : 'Run now'}
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card className="p-4">
            <p className="text-xs text-gray-500 uppercase">Pending payout</p>
            <p className="text-2xl font-bold text-yellow-600">K{totalPending.toFixed(2)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-gray-500 uppercase">Total paid (history)</p>
            <p className="text-2xl font-bold text-green-600">K{totalPaid.toFixed(2)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-gray-500 uppercase">Settlements</p>
            <p className="text-2xl font-bold">{items.length}</p>
          </Card>
        </div>

        {loading && <p className="text-gray-500">Loading...</p>}

        {!loading && items.length === 0 && (
          <Card className="p-8 text-center text-gray-500">
            <DollarSign className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No settlements yet. Click "Run now" to compute the first batch.</p>
          </Card>
        )}

        <div className="space-y-3" data-testid="settlements-list">
          {items.map(s => (
            <Card key={s.id} className="p-4">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{s.operatorName}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[s.status] || 'bg-gray-100'}`}>
                      {s.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {s.bookings} bookings · K{(s.grossRevenue || 0).toFixed(2)} gross · K{(s.commission || 0).toFixed(2)} commission
                  </p>
                  <p className="text-lg font-bold text-pink-600 mt-1">Net K{(s.netPayout || 0).toFixed(2)}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Period {new Date(s.periodStart).toLocaleDateString()} → {new Date(s.periodEnd).toLocaleDateString()}
                  </p>
                  {s.mobileMoneyTxnId && <p className="text-xs text-emerald-600 mt-1">Txn: {s.mobileMoneyTxnId}</p>}
                </div>
                <div className="flex flex-col gap-2">
                  {s.status !== 'paid' && (
                    <Button
                      size="sm"
                      onClick={() => handleMarkPaid(s.id)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`mark-paid-${s.id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark paid
                    </Button>
                  )}
                  {s.status === 'paid' && s.settledAt && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                      {new Date(s.settledAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettlements;
